import type { RecurringJob, RecurringStatus } from "../../../api/client.ts";
import type { Status } from "../../StatusIcon.tsx";

/** A recurring job's or chain's status as a health icon and label. */
export function recurringStatus(status: RecurringStatus): Status & { label: string } {
  switch (status) {
    case "ok":
      return { kind: "ok", label: "Ok" };
    case "late":
      return { kind: "attention", label: "Late" };
    case "running long":
      return { kind: "attention", label: "Running long" };
    case "no runs":
      return { kind: "attention", label: "No runs yet" };
    case "failing":
      return { kind: "error", label: "Failing" };
    case "off":
      return { kind: "off", label: "Switched off" };
    case "stopped":
      return { kind: "off", label: "Chain ended" };
  }
}

export const NEEDS_ATTENTION: RecurringStatus[] = ["failing", "late", "running long"];

// Words a Restate service name spells as one capitalised token.
const ACRONYMS: Record<string, string> = {
  ats: "ATS",
  crm: "CRM",
  csv: "CSV",
  ecb: "ECB",
  fx: "FX",
  llm: "LLM",
  nav: "NAV",
  nve: "NVE",
  prh: "PRH",
  ted: "TED",
  uhm: "UHM",
};

/** "CrmIncrementalSync" → "CRM incremental sync". */
export function jobLabel(name: string): string {
  // No lookbehind: Safari before 16.4 can't parse one, and a parse error
  // blanks every page (the bundle is one module).
  const words = name
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .map((w) => w.toLowerCase());
  return words
    .map((w, i) => ACRONYMS[w] ?? (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");
}

/** What people call a job: its declared title, else its name spelt out. */
export function jobTitle(job: Pick<RecurringJob, "name" | "title">): string {
  return job.title ?? jobLabel(job.name);
}

/** "csv_import_step" → "CSV import step": a durable task kind. */
export function taskKindLabel(kind: string): string {
  return kind
    .split("_")
    .map((w, i) => ACRONYMS[w] ?? (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");
}

/** "Every 5 min, on the slot", "Every 30 s". */
export function cadenceLabel(job: Pick<RecurringJob, "every_s" | "aligned">): string {
  const s = job.every_s;
  const every = s < 60 ? `${s} s` : s < 3600 ? `${Math.round(s / 60)} min` : `${+(s / 3600).toFixed(1)} h`;
  return `Every ${every}${job.aligned ? ", on the slot" : ""}`;
}

/** "42 s", "12 min", "3.4 h", or "–". */
export function seconds(value: number | null | undefined): string {
  if (value == null) return "–";
  if (value < 10) return `${value.toFixed(1)} s`;
  if (value < 60) return `${Math.round(value)} s`;
  if (value < 3600) return `${Math.round(value / 60)} min`;
  return `${(value / 3600).toFixed(1)} h`;
}

/** A span of whole seconds: "42s", "12m", "5h", "3d". */
function span(s: number): string {
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 172_800) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86_400)}d`;
}

/** When something is due: "in 3m", "in 2h", "due now", "3m overdue". */
export function timeUntil(value: string | null | undefined, now = Date.now()): string {
  if (!value) return "–";
  const diff = Math.round((new Date(value).getTime() - now) / 1000);
  if (Math.abs(diff) < 5) return "due now";
  return diff > 0 ? `in ${span(diff)}` : `${span(-diff)} overdue`;
}

/** How long since something started: "3m". */
export function elapsed(value: string, now = Date.now()): string {
  return span(Math.max(0, Math.round((now - new Date(value).getTime()) / 1000)));
}
